/**
 * TRANSITIONAL — the Valkey renaming (plan of 2026-10-25, decision 3: Odoo's
 * field `haRedis` → `haCache`, the odoo-project template's `ha.redis` →
 * `ha.cache`). The ONE place where the Odoo module learns which name the
 * other side uses, so that this Backstage works with platform-catalog
 * before its migration (descriptor `haRedis`, template `ha.redis`) as after
 * it (`haCache`, `ha.cache`), whichever is deployed first.
 *
 * Inside the module, the names are always the new ones (`haCache`,
 * `cache`); they are translated here, at the edges: the descriptor and the
 * values the configuration route answers, the fields sent to
 * `env-configure`, the key the HA picker writes in the template's `ha`.
 *
 * Its server-side counterpart, for the fields a task receives, is
 * `withDeclaredFieldIds` (`RENAMED_FIELD_IDS`) of the shared engine, read
 * by `validateFieldValues` (`waadoo:env:configure`, `waadoo:env:quota`).
 *
 * TO REMOVE with the `redis` alias of the contract (`kind: redis`, values
 * block `redis:`), once every descriptor and template says `haCache` and
 * `ha.cache`: then `CACHE_FIELD` and `cache` everywhere, this file deleted.
 */

import {
  compileDescriptor,
  type AppTypeDescriptor,
  type FieldValues,
} from '@internal/plugin-waadoo-config-common';

/** The field the module knows: Odoo's Valkey sessions switch. */
export const CACHE_FIELD = 'haCache';
/** Its name in a descriptor not yet migrated. */
export const LEGACY_CACHE_FIELD = 'haRedis';

/** How the module reads and writes against one descriptor. */
export interface CacheFieldNaming {
  /** The id the descriptor declares for the switch. */
  field: typeof CACHE_FIELD | typeof LEGACY_CACHE_FIELD;
  /** The descriptor under the module's names (itself when migrated). */
  descriptor: AppTypeDescriptor;
  /** Values keyed as the descriptor declares, keyed as the module does. */
  toModel<V extends FieldValues | undefined>(values: V): V;
  /** Values keyed as the module does, keyed as the descriptor declares. */
  toDescriptor<V extends Partial<FieldValues>>(values: V): V;
}

/** `values` with the key `from`, if any, renamed `to`, order kept. */
function renamed<V>(values: V, from: string, to: string): V {
  return Object.fromEntries(
    Object.entries(values as Record<string, unknown>).map(([key, value]) => [
      key === from ? to : key,
      value,
    ]),
  ) as V;
}

const migrated = new WeakMap<AppTypeDescriptor, AppTypeDescriptor>();

/**
 * The naming of `descriptor`: the legacy one when it declares `haRedis` and
 * not `haCache`. Its renamed copy compiles the same source with every
 * `haRedis` identifier (field id, expressions, rules, `lightweight`)
 * replaced — the identifier appears in no label or text of the Odoo
 * descriptor, only as an id —, cached per descriptor.
 */
export function cacheFieldNaming(
  descriptor: AppTypeDescriptor,
): CacheFieldNaming {
  const legacy =
    !descriptor.fieldById.has(CACHE_FIELD) &&
    descriptor.fieldById.has(LEGACY_CACHE_FIELD);
  if (!legacy) {
    return {
      field: CACHE_FIELD,
      descriptor,
      toModel: <V>(values: V) => values,
      toDescriptor: <V>(values: V) => values,
    };
  }
  let model = migrated.get(descriptor);
  if (!model) {
    const { source } = descriptor;
    model = compileDescriptor({
      ...source,
      spec: JSON.parse(
        JSON.stringify(source.spec).replace(
          new RegExp(`\\b${LEGACY_CACHE_FIELD}\\b`, 'g'),
          CACHE_FIELD,
        ),
      ),
    });
    migrated.set(descriptor, model);
  }
  return {
    field: LEGACY_CACHE_FIELD,
    descriptor: model,
    toModel: <V>(values: V) =>
      values === undefined
        ? values
        : renamed(values, LEGACY_CACHE_FIELD, CACHE_FIELD),
    toDescriptor: <V>(values: V) =>
      renamed(values, CACHE_FIELD, LEGACY_CACHE_FIELD),
  };
}

/** The key of the switch in the template's `ha` object. */
export type HaCacheKey = 'cache' | 'redis';

/**
 * The key the odoo-project template declares for the switch in its `ha`
 * schema: `redis` when it declares `redis` and not `cache` (a template not
 * yet migrated), else `cache`.
 */
export function haCacheKey(schema: unknown): HaCacheKey {
  const properties =
    schema && typeof schema === 'object'
      ? (schema as { properties?: unknown }).properties
      : undefined;
  const has = (key: string) =>
    !!properties &&
    typeof properties === 'object' &&
    Object.prototype.hasOwnProperty.call(properties, key);
  return !has('cache') && has('redis') ? 'redis' : 'cache';
}
